// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useAuth } from '@parsleyhealth/front-end-utils';
import { Button } from 'components/atoms/Button';
import { RippleLoader } from 'components/atoms/RippleLoader';
import { InputField } from 'components/molecules/InputField';
import queryString from 'query-string';
import React, { useEffect, useState, useCallback } from 'react';
import * as reactHookForm from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import { validateFormatEmail, suggestEmail } from 'utils/validator';

import { Colors } from 'constants/theme';

type LoginPageForm = {
  email: string;
  password: string;
};

const getPasswordErrorMessage = ({
  errors
}: reactHookForm.FormState<LoginPageForm>) => {
  const passwordErrors = errors.password;
  if (!passwordErrors) return undefined;
  switch (passwordErrors.type) {
    case 'required':
      return 'Password is required';
  }
};

const getEmailErrorMessage = ({
  errors
}: reactHookForm.FormState<LoginPageForm>) => {
  const emailErrors = errors.email;
  if (!emailErrors) return undefined;
  switch (emailErrors.type) {
    case 'required':
      return 'Email field is required';
    case 'validate':
      return emailErrors.message;
  }
};

const validateEmail = (email: string) => {
  if (!validateFormatEmail(email)) return 'Please enter a valid email';
  if (suggestEmail(email)) return `Did you mean ${suggestEmail(email)}`;
  return true;
};

export const LoginPage: React.FC = () => {
  const { isLoggedIn, login } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState<string | undefined>('');
  const { formState, handleSubmit, register } =
    reactHookForm.useForm<LoginPageForm>();

  const redirectAfterLogin = useCallback(() => {
    const qs = queryString.parse(location.search);
    const redirect = qs.redirect as string;
    if (redirect && !redirect.includes('logout')) {
      history.push(redirect);
    } else {
      history.push('/dashboard');
    }
  }, [location, history]);

  useEffect(() => {
    if (isLoggedIn) {
      redirectAfterLogin();
    }
  }, [isLoggedIn, redirectAfterLogin]);

  const onSubmit = ({ email, password }: LoginPageForm) => {
    login(email, password)
      .then(() => {
        window.heap.identify(email);
        redirectAfterLogin();
      })
      .catch((error) => {
        setError(error.message);
      });
  };

  return (
    <MainLoginPage>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <Greeting>Hey, you!</Greeting>
          <Error>{error}</Error>
          <InputField
            label="Email"
            type="email"
            {...register('email', {
              required: true,
              validate: validateEmail
            })}
            errorMessage={getEmailErrorMessage(formState)}
          />
          <M15 />
          <InputField
            label="Password"
            type="password"
            {...register('password', {
              required: true
            })}
            errorMessage={getPasswordErrorMessage(formState)}
          />
          <SignUpButton
            autoFocus={true}
            onClick={handleSubmit(onSubmit)}
            variant="primary"
          >
            {formState.isValidating || formState.isSubmitting ? (
              <RippleLoader />
            ) : (
              'Sign In'
            )}
          </SignUpButton>
          <ForgotPasswordLabel to="/forgot-password">
            I forgot my password
          </ForgotPasswordLabel>
        </Container>
      </form>
    </MainLoginPage>
  );
};

const Error = styled.p`
  font-family: 'EuclidCircularB', sans-serif;
  font-size: 14px;
  color: ${Colors.poppy6};
`;

const M15 = styled.div`
  margin: 15px;
`;

const MainLoginPage = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  background-color: #fff;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Container = styled.div`
  max-width: 768px;
  width: 315px;
  display: flex;
  flex-direction: column;
`;

const Greeting = styled.h2`
  font-family: TeodorLight;
  font-size: 36px;
  line-height: 42px;
  text-align: center;
  color: #333333;
`;

const SignUpButton = styled(Button)`
  margin-top: 40px;
  height: 50px;
  width: 115px;
  align-self: center;
`;

const ForgotPasswordLabel = styled(Link)`
  font-family: EuclidCircularB;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  text-decoration-line: underline;
  color: #4f7f71;

  margin-top: 20px;
`;

export default LoginPage;
